import api from './api';

export interface BackupStatus {
    gcloud_authenticated: boolean;
    bucket_exists: boolean;
    bucket_name: string;
    project_id: string | null;
    total_backups: number;
    total_size_mb: number;
    last_backup: BackupInfo | null;
    configured: boolean;
}

export interface BackupInfo {
    filename: string;
    gcs_path: string;
    size: number;
    size_mb: number;
    created_at: string;
    type: 'manual' | 'scheduled';
}

export interface BackupListResponse {
    total: number;
    backups: BackupInfo[];
}

export interface BackupResult {
    success: boolean;
    message: string;
    filename?: string;
    gcs_path?: string;
    original_size?: number;
    compressed_size?: number;
    compression_ratio?: string;
}

const backupService = {
    // Get backup system status
    async getStatus(): Promise<BackupStatus> {
        const response = await api.get('/backups/status');
        return response.data;
    },

    // Create backup (background)
    async createBackup(type: 'manual' | 'scheduled' = 'manual'): Promise<BackupResult> {
        const response = await api.post('/backups/create', { backup_type: type });
        return response.data;
    },

    // Create backup (synchronous)
    async createBackupSync(type: 'manual' | 'scheduled' = 'manual'): Promise<BackupResult> {
        const response = await api.post('/backups/create-sync', { backup_type: type });
        return response.data;
    },

    // List backups
    async listBackups(backupType?: string, limit: number = 50): Promise<BackupListResponse> {
        const params = new URLSearchParams();
        if (backupType) params.append('backup_type', backupType);
        params.append('limit', limit.toString());
        
        const response = await api.get(`/backups/list?${params.toString()}`);
        return response.data;
    },

    // Restore backup
    async restoreBackup(gcsPath: string): Promise<BackupResult> {
        const response = await api.post('/backups/restore', { gcs_path: gcsPath });
        return response.data;
    },

    // Delete backup
    async deleteBackup(gcsPath: string): Promise<BackupResult> {
        const response = await api.delete('/backups/delete', { data: { gcs_path: gcsPath } });
        return response.data;
    },

    // Cleanup old backups
    async cleanupOldBackups(retentionDays: number = 30): Promise<BackupResult> {
        const response = await api.post('/backups/cleanup', { retention_days: retentionDays });
        return response.data;
    },

    // Cleanup old backups (synchronous)
    async cleanupOldBackupsSync(retentionDays: number = 30): Promise<any> {
        const response = await api.post('/backups/cleanup-sync', { retention_days: retentionDays });
        return response.data;
    }
};

export default backupService;
